import { useState } from 'react';

import { useToast } from '../../contexts/ToastContext';
import { toUserErrorMessage } from '../../lib/apiError';
import { downloadFile } from '../../lib/downloadFile';
import { toQueryString } from '../../lib/queryString';
import { toRateInput } from '../../services/api/mappers';
import type { ExportKind } from '../../types/extranet-api.types';
import { useFilterStore } from '../filters/filterStore';
import { IconDownload } from '../incidents/icons';

interface ExportDefinition {
  kind: ExportKind;
  title: string;
  description: string;
  filename: string;
}

const EXPORTS: ExportDefinition[] = [
  {
    kind: 'late-cancellations',
    title: 'Annulations tardives',
    description: 'Les annulations passager facturées de la période, avec la course, les dates et le prix.',
    filename: 'allocab-extranet-annulations-tardives.csv',
  },
  {
    kind: 'no-shows',
    title: 'No-show passager',
    description: 'Les no-show passager de la période, avec la course, les dates et le prix.',
    filename: 'allocab-extranet-no-show.csv',
  },
];

/**
 * `GET /v1/exports/*` — CSV, sur la période et le périmètre du header.
 *
 * Le back gate ces routes sur la permission `extranet-exports`, que
 * `GET /v1/users/me` n'expose pas : le front ne peut pas savoir à l'avance si
 * l'utilisateur y a droit. On propose donc les exports à tout le domaine
 * Performance et on laisse le 403 remonter — son message est explicite et
 * localisé. À revoir si Yohan expose la permission dans `/me`.
 */
export const PerformanceExports = () => {
  const toast = useToast();
  const { scopes, dateRange } = useFilterStore();
  const [pendingKind, setPendingKind] = useState<ExportKind | null>(null);

  const handleDownload = async (item: ExportDefinition) => {
    setPendingKind(item.kind);
    try {
      const query = toQueryString({ ...toRateInput({ scopes, dateRange }) });
      await downloadFile(`/v1/exports/${item.kind}${query}`, item.filename);
      toast.success('Export téléchargé', item.filename);
    } catch (err) {
      const { title, description } = toUserErrorMessage(err, 'Échec du téléchargement');
      toast.error(title, description);
    } finally {
      setPendingKind(null);
    }
  };

  return (
    <article className="rounded-md border border-zinc-200 bg-white p-6 flex flex-col gap-5">
      <header>
        <h2 className="text-sm font-medium text-zinc-900">Exports</h2>
        <p className="text-xs text-zinc-500 mt-0.5">
          Données détaillées au format CSV, sur la période et le périmètre filtrés.
        </p>
      </header>

      <ul className="grid grid-cols-1 lg:grid-cols-2 gap-3">
        {EXPORTS.map(item => (
          <li
            key={item.kind}
            className="border border-zinc-200 rounded-md p-4 flex flex-col gap-3 hover:border-zinc-300 transition-colors"
          >
            <div className="flex flex-col gap-1.5">
              <h3 className="text-sm font-medium text-zinc-900">{item.title}</h3>
              <p className="text-xs text-zinc-500 leading-relaxed">{item.description}</p>
            </div>
            <button
              type="button"
              onClick={() => handleDownload(item)}
              disabled={pendingKind === item.kind}
              className="inline-flex items-center justify-center gap-1.5 h-8 px-3 text-xs font-medium text-zinc-700 bg-white border border-zinc-200 rounded-md hover:bg-zinc-50 hover:border-zinc-300 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-100 mt-auto self-start"
            >
              <IconDownload width={12} height={12} />
              <span>{pendingKind === item.kind ? 'Téléchargement…' : 'Télécharger .csv'}</span>
            </button>
          </li>
        ))}
      </ul>
    </article>
  );
};
